import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { AUTH_PATHS } from '@/components/Auth/constants'
import ScoringLogo from '@/images/scoring-horizontal.svg?react'
import GlobeSVG from '@/images/home/Globe.svg?react'
import CloseXSVG from '@/images/home/CloseX.svg?react'
import AvatarSVG from '@/images/home/Avatar.svg'
import UserLoginSVG from '@/images/home/UserLogin.svg?react'
import UserCreateSVG from '@/images/home/UserCreate.svg?react'
import { useAuth } from '../../middlewares/auth/auth-context'
import Confirm from '../common/Confirm'
import Portal from '../common/Portal'
import BookACallButton from './BookACallButton'
import CreateEventButton from './CreateEventButton'
import ExternalLink from './ExternalLink'
import { LANGUAGES } from './useNavMenuItems'

// Kit .mmenu-btn: 44px touch target, radius 10. Ink (default) or ghost (outline).
const MENU_BTN =
    'flex items-center justify-center gap-[9px] h-11 w-full rounded-[10px] text-[14.5px] no-underline cursor-pointer s-focus-ring'
const MENU_BTN_INK = `${MENU_BTN} bg-(--btn-bg) text-white! hover:text-white! border-0`
const MENU_BTN_GHOST = `${MENU_BTN} bg-transparent text-(--t-strong)! hover:text-(--t-strong)! hover:bg-(--field) border border-(--nav-bd)`

// Kit .mmenu-user-menu / .mmenu-langs: bordered white panel, 6px padding, 2px between rows.
const PANEL = 'flex flex-col gap-0.5 p-1.5 rounded-[10px] border border-(--nav-bd) bg-white'
const PANEL_ROW =
    'flex items-center w-full min-h-11 px-3 rounded-[5px] bg-transparent border-0 text-[14px] text-left no-underline cursor-pointer text-(--ink-3) hover:bg-(--field) hover:text-(--t-strong)'
// `bg-(--field)!`: PANEL_ROW's bg-transparent otherwise wins the cascade.
const PANEL_ROW_CURRENT = 'bg-(--field)! text-(--t-strong)! font-semibold'
// Kit .mmenu-langs button.on: field surface + strong ink, no weight change.
const LANG_ROW_CURRENT = 'bg-(--field)! text-(--t-strong)!'

/**
 * Full-screen white mobile side menu (the Figma "Mobile Side Menu"). Opened from
 * the NavbarMobile hamburger. Order follows the kit: links, then the account
 * block (connected: Create + user row + the account rows always expanded;
 * logged-out: Create (routes through the login) then Log In + Create Account), a separator,
 * Book a Call and Language. Since 15 Oct 2026 this menu is the only place in the site menu that
 * shows "Create an Event" (the desktop bar dropped it: the home category bar carries it).
 * Internal navigation honours the `processInProgress` unsaved-work guard.
 */
export default function NavbarMobileMenu({ open, onClose, menu, processInProgress, setProcessInProgess }) {
    const { t, i18n } = useTranslation(TranslationNS.NAVBAR)
    const navigate = useNavigate()
    const location = useLocation()
    const authContext = useAuth()

    const [langOpen, setLangOpen] = useState(false)

    // Esc closes + lock body scroll while open.
    useEffect(() => {
        if (!open) return

        const handleKey = (e) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handleKey)

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', handleKey)
            document.body.style.overflow = previousOverflow
        }
    }, [open])

    // Collapse the language list whenever the menu closes.
    useEffect(() => {
        if (!open) setLangOpen(false)
    }, [open])

    // Close the menu on route change.
    useEffect(() => {
        onClose()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    const NavLinkConfirm = ({ to, children, className = '', activeClassName = '' }) => {
        const isActive = location.pathname === to
        const classes = `${className} ${isActive ? activeClassName : ''}`

        if (processInProgress) {
            return (
                <Confirm
                    onConfirm={() => {
                        navigate(to)
                        setProcessInProgess(false)
                        onClose()
                    }}
                    title={t('Are you sure you want to leave?')}
                    className={classes}
                >
                    {children}
                </Confirm>
            )
        }

        return (
            <NavLink to={to} onClick={onClose} className={classes} aria-current={isActive ? 'page' : undefined}>
                {children}
            </NavLink>
        )
    }

    const DisconnectRow = ({ children, className = '' }) => {
        if (processInProgress) {
            return (
                <Confirm
                    onConfirm={() => {
                        authContext.onLogout('/')
                        setProcessInProgess(false)
                        onClose()
                    }}
                    title={t('Are you sure you want to leave?')}
                    className={className}
                >
                    {children}
                </Confirm>
            )
        }

        return (
            <button
                type="button"
                className={className}
                onClick={() => {
                    authContext.onLogout('/')
                    onClose()
                }}
            >
                {children}
            </button>
        )
    }

    if (!open) return null

    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2)

    const authState = AUTH_PATHS.includes(location.pathname) ? location.state : { backgroundLocation: location }

    const linksBlock = (
        <nav className="flex flex-col gap-[18px] text-[15px]">
            {menu.externalLinks.map((l) => (
                <ExternalLink key={l.key} value={l.label} href={l.href} className="text-[15px]" />
            ))}
        </nav>
    )

    const languageControl = (
        <>
            <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                aria-expanded={langOpen}
                className={MENU_BTN_INK}
            >
                <GlobeSVG className="size-[17px]" aria-hidden />
                <span>{t('Language')}</span>
            </button>

            {langOpen && (
                <div className={PANEL} role="group" aria-label={t('Language')}>
                    {LANGUAGES.map(({ code, label }) => (
                        <button
                            key={code}
                            type="button"
                            onClick={() => {
                                i18n.changeLanguage(code)
                                setLangOpen(false)
                            }}
                            aria-current={currentLanguage === code ? 'true' : undefined}
                            className={`${PANEL_ROW} ${currentLanguage === code ? LANG_ROW_CURRENT : ''}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </>
    )

    const divider = <hr className="m-0 w-full border-0 border-t border-[#ececf0]" />

    return (
        // In a portal so z-200 counts at the page root: inside #navbar (its own stacking context, z 50 in
        // the organizer shell) the organizer side menu covered it. Full screen under 761 px; above, a 393 px
        // side bar on the right (the mobile frame) over the scrim of the organizer drawers.
        <Portal id="site-menu">
            <div className="fixed inset-0 z-[200] flex justify-end">
                <button
                    type="button"
                    aria-label={t('Close menu')}
                    tabIndex={-1}
                    onClick={onClose}
                    className="absolute inset-0 hidden min-[761px]:block border-0 p-0 cursor-default bg-[rgba(14,16,23,0.28)] backdrop-blur-[4px]"
                />
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Menu"
                    className="relative h-full w-full overflow-y-auto bg-white min-[761px]:w-[393px] min-[761px]:shadow-[-10px_0_30px_-18px_rgba(14,16,23,0.35)]"
                >
                    {/* Padding lives on an inner block: browsers drop padding-bottom on a scrolling
                        flex container once its content overflows (long admin menus hit this). */}
                    <div className="flex min-h-full flex-col gap-6 px-5 py-6 box-border">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <Link to="/" onClick={onClose} aria-label="Scoring.fit" className="s-focus-ring">
                                <ScoringLogo className="w-[135.2px] h-auto block" />
                            </Link>
                            <button
                                type="button"
                                aria-label={t('Close menu')}
                                onClick={onClose}
                                className="flex size-[38px] items-center justify-center bg-transparent border-0 p-0 cursor-pointer text-(--t-strong) s-focus-ring"
                            >
                                <CloseXSVG className="w-[23px] h-[23px]" />
                            </button>
                        </div>

                        {linksBlock}

                        {authContext.isLoggedIn ? (
                            <>
                                <div className="flex items-center justify-between gap-3">
                                    {/* the menu closes itself before the modal opens over the page */}
                                    <CreateEventButton onBeforeOpen={onClose} />

                                    <NavLinkConfirm
                                        to="/my-account"
                                        className="inline-flex items-center gap-2.5 no-underline"
                                    >
                                        <span className="text-[14px] text-black/50">{authContext.user.username}</span>
                                        <span className="block size-[39px] flex-none overflow-hidden rounded-[10px] border border-(--nav-bd)">
                                            <img
                                                src={authContext.user.logo || AvatarSVG}
                                                alt="Profile picture"
                                                className="block size-full object-cover"
                                            />
                                        </span>
                                    </NavLinkConfirm>
                                </div>

                                {/* Always expanded: the account rows used to hide behind a second tap. */}
                                <div className={`${PANEL} -mt-2.5`}>
                                    {menu.menuItems.map((it) =>
                                        it.type === 'divider' ? (
                                            <hr
                                                key={it.key}
                                                className="m-0 my-0.5 w-full border-0 border-t border-(--nav-bd)"
                                            />
                                        ) : it.key === 'disconnect' ? (
                                            <DisconnectRow key={it.key} className={PANEL_ROW}>
                                                {it.label}
                                            </DisconnectRow>
                                        ) : it.action ? (
                                            <button
                                                key={it.key}
                                                type="button"
                                                className={PANEL_ROW}
                                                onClick={() => {
                                                    onClose()
                                                    it.action()
                                                }}
                                            >
                                                {it.label}
                                            </button>
                                        ) : (
                                            <NavLinkConfirm
                                                key={it.key}
                                                to={it.to}
                                                className={PANEL_ROW}
                                                activeClassName={PANEL_ROW_CURRENT}
                                            >
                                                {it.label}
                                            </NavLinkConfirm>
                                        ),
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {/* the menu closes itself before the login page takes over */}
                                <CreateEventButton variant="menu" onBeforeOpen={onClose} />

                                <div className="flex items-center gap-3">
                                    {/* backgroundLocation drives the desktop auth modal; harmless on
                        mobile, correct if the window is resized past the nav breakpoint. */}
                                    <Link to="/connect" state={authState} className={`${MENU_BTN_GHOST} flex-1`}>
                                        <UserLoginSVG className="size-[15px]" aria-hidden />
                                        {t('Log In')}
                                    </Link>
                                    <Link to="/sign-up" state={authState} className={`${MENU_BTN_INK} flex-1`}>
                                        <UserCreateSVG className="size-[15px]" aria-hidden />
                                        {t('Create Account')}
                                    </Link>
                                </div>
                            </div>
                        )}

                        {divider}

                        <BookACallButton t={t} variant="ink" />

                        {languageControl}
                    </div>
                </div>
            </div>
        </Portal>
    )
}
